/* Shared read-only Shopify Market price resolver for both production PC2 runtimes. */
(() => {
  if (window.CasebangPc2MarketPrices) return;
  const endpoint = 'https://casebang.work/api/storefront-selector/contextual-prices';
  const prices = new Map();
  const inflight = new Map();
  const normalize = (value) => {
    const id = String(value ?? '').trim();
    if (/^\d+$/.test(id)) return `gid://shopify/ProductVariant/${id}`;
    return /^gid:\/\/shopify\/ProductVariant\/\d+$/.test(id) ? id : null;
  };
  const context = () => {
    const value = window.__casebangPc2MarketContext;
    return value && /^[A-Z]{2}$/.test(value.country) && /^[A-Z]{3}$/.test(value.currency)
      ? value : null;
  };
  const key = (country, id) => `${country}:${id}`;
  const variants = (selector) => {
    try {
      const data = JSON.parse(document.querySelector(selector)?.textContent || 'null');
      return Array.isArray(data) ? data : [];
    } catch { return []; }
  };
  const ownVariant = (id) => variants('#product-variants-json').find((item) => normalize(item.id) === id);
  const componentRows = () => {
    let grouped = {};
    try { grouped = JSON.parse(document.querySelector('#component-variants-json')?.textContent || '{}') || {}; }
    catch { grouped = {}; }
    return {
      BG: variants('#product-variants-json'),
      CA: [...(Array.isArray(grouped.CA) ? grouped.CA : []), ...variants('#accessory-variants-json')],
      RS: Array.isArray(grouped.RS) ? grouped.RS : [],
      SB: Array.isArray(grouped.SB) ? grouped.SB : [],
      ZJ: Array.isArray(grouped.ZJ) ? grouped.ZJ : [],
    };
  };
  const minorMoney = (cents, currencyCode) => ({ amount: (Number(cents) / 100).toFixed(2), currencyCode });
  const exact = (id) => {
    const market = context();
    const row = ownVariant(id);
    if (!market || !row || !Number.isFinite(Number(row.price))) return null;
    return {
      price: minorMoney(row.price, market.currency),
      compareAtPrice: row.compare_at_price == null ? null : minorMoney(row.compare_at_price, market.currency),
    };
  };
  const staticUsd = (id) => {
    const market = context();
    if (market?.currency !== 'USD') return null;
    const rows = componentRows();
    const row = Object.values(rows).flat().find((item) => normalize(item.id) === id);
    if (!row || !Number.isFinite(Number(row.price))) return null;
    return {
      price: minorMoney(row.price, 'USD'),
      compareAtPrice: row.compare_at_price == null ? null : minorMoney(row.compare_at_price, 'USD'),
    };
  };
  const lookup = (id) => {
    const market = context();
    const gid = normalize(id);
    if (!market || !gid) return null;
    return exact(gid) || prices.get(key(market.country, gid)) || staticUsd(gid);
  };
  const request = async (values) => {
    const market = context();
    if (!market) return;
    const ids = [...new Set(values.map(normalize).filter(Boolean))].filter((id) => !exact(id));
    const pending = ids.filter((id) => !prices.has(key(market.country, id)) && !inflight.has(key(market.country, id)));
    for (let index = 0; index < pending.length; index += 100) {
      const batch = pending.slice(index, index + 100);
      const job = fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ country: market.country, variantIds: batch }),
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Market pricing HTTP ${response.status}`);
        const payload = await response.json();
        if (payload.country !== market.country || !Array.isArray(payload.items)) throw new Error('Market pricing context mismatch');
        for (const item of payload.items) {
          const id = normalize(item.variantId);
          if (!id || !batch.includes(id) || item.price?.currencyCode !== market.currency ||
              (item.compareAtPrice && item.compareAtPrice.currencyCode !== market.currency))
            throw new Error('Market pricing currency mismatch');
          prices.set(key(market.country, id), { price: item.price, compareAtPrice: item.compareAtPrice });
        }
      }).catch((error) => console.error('[PC2 Market Price]', error)).finally(() => {
        for (const id of batch) inflight.delete(key(market.country, id));
      });
      for (const id of batch) inflight.set(key(market.country, id), job);
    }
    await Promise.all(ids.map((id) => inflight.get(key(market.country, id))).filter(Boolean));
  };
  const same = (a, b) => String(a ?? '').trim().replace(/\s+/g, ' ').toLowerCase() === String(b ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  const rowForPc2 = (variant, type) => {
    const rows = componentRows()[type] || [];
    const matches = rows.filter((row) => same(row.sku?.replace(/-TEST-\d+$/, ''), variant.sku?.replace(/-TEST-\d+$/, '')));
    const model = variant.model?.name;
    if (model) {
      const modelMatches = matches.filter((row) => same(row.option1, model) || (Array.isArray(row.options) && same(row.options[0], model)));
      if (modelMatches.length === 1) return modelMatches[0];
    }
    return matches.length === 1 ? matches[0] : null;
  };
  const format = (money) => {
    if (!money || !Number.isFinite(Number(money.amount)) || !/^[A-Z]{3}$/.test(money.currencyCode || '')) return '—';
    try { return new Intl.NumberFormat(context()?.locale || Shopify.locale || 'en', {
      style: 'currency', currency: money.currencyCode, currencyDisplay: 'narrowSymbol',
    }).format(Number(money.amount)); }
    catch { return '—'; }
  };
  const formatPc2Variant = (variant, type) => {
    const row = rowForPc2(variant, type);
    return row ? format(lookup(row.id)?.price) : '—';
  };
  const prefetchVisible = async (products, model) => {
    if (!model) return;
    const ids = products.flatMap((product) => (product.variants || [])
      .filter((variant) => !variant.model || variant.model.id === model.id || variant.model.code === 'UNIVERSAL')
      .map((variant) => rowForPc2(variant, product.type)?.id).filter(Boolean));
    await request(ids);
  };
  const sum = (items) => {
    if (!items.length) return null;
    const currencyCode = items[0].price.currencyCode;
    if (items.some((item) => item.price.currencyCode !== currencyCode ||
        (item.compareAtPrice && item.compareAtPrice.currencyCode !== currencyCode))) {
      console.error('[PC2 Market Price] mixed currency selection');
      return null;
    }
    const digits = new Intl.NumberFormat('en', { style: 'currency', currency: currencyCode }).resolvedOptions().maximumFractionDigits;
    const factor = 10 ** digits;
    const add = (selector) => (items.reduce((total, item) => total + Math.round(Number(selector(item).amount) * factor), 0) / factor).toFixed(digits);
    return {
      price: { amount: add((item) => item.price), currencyCode },
      compareAtPrice: { amount: add((item) => item.compareAtPrice || item.price), currencyCode },
    };
  };
  const resolve = async (values) => {
    const ids = values.map(normalize).filter(Boolean);
    if (!ids.length) return null;
    await request(ids);
    const items = ids.map(lookup);
    if (items.some((item) => !item)) return null;
    return items.length === 1 ? items[0] : sum(items);
  };
  window.CasebangPc2MarketPrices = { normalize, lookup, request, resolve, format, formatPc2Variant, prefetchVisible };
})();
